import { useMemo, useState } from "react";

import { useNavigate } from "react-router-dom";

import dayjs from "dayjs";
import "dayjs/locale/es";
import { ArrowRight, Download, History, Search } from "lucide-react";

import { Button } from "~/components/ui/button";
import { Card, CardContent } from "~/components/ui/card";
import { EmptyState } from "~/components/ui/empty-state";
import { Input } from "~/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { TableSkeleton } from "~/components/ui/skeletons";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { TablePagination } from "~/components/ui/table-pagination";
import { usePagination } from "~/hooks/usePagination";
import { Database } from "~/types/supabase";
import { exportCsv } from "~/utils/csv";

import { TotemStatusBadge, getStatusLabel } from "./TotemStatusBadge";
import { useAllTotemMovements } from "../hooks/useTotemDetail";

dayjs.locale("es");

type TotemStatus = Database["public"]["Enums"]["totem_status"];

const PAGE_SIZE = 15;

// Same order as TotemStatusBadge's statusConfig, for the "estado destino" filter.
const TOTEM_STATUSES: TotemStatus[] = [
  "available",
  "filled",
  "in_transit",
  "at_client",
  "pickup_ready",
  "pickup_scheduled",
  "pickup_transit",
  "returned",
  "cleaning",
  "damaged",
  "lost",
];

const performerName = (
  profile: { name: string; lastname: string } | null | undefined
): string =>
  profile ? `${profile.name} ${profile.lastname}`.trim() : "Sistema (automático)";

export function MovementsHistoryTab() {
  const navigate = useNavigate();

  // Date range is the transparent bound on how much history is loaded (no hidden
  // row cap) — widening it fetches more. Default: last 90 days.
  const [from, setFrom] = useState(() =>
    dayjs().subtract(90, "day").format("YYYY-MM-DD")
  );
  const [to, setTo] = useState(() => dayjs().format("YYYY-MM-DD"));
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<TotemStatus | "all">("all");

  const { data: movements, isLoading } = useAllTotemMovements({
    from: dayjs(from).startOf("day").toISOString(),
    to: dayjs(to).endOf("day").toISOString(),
  });

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (movements ?? []).filter((m) => {
      if (statusFilter !== "all" && m.to_status !== statusFilter) return false;
      if (!q) return true;
      const haystack = [
        m.totem?.code,
        m.client?.name,
        m.notes,
        performerName(m.performed_by_profile),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [movements, search, statusFilter]);

  const { page, setPage, pageCount, pageItems, total } = usePagination(
    filtered,
    PAGE_SIZE
  );

  const handleExport = () => {
    exportCsv(
      `movimientos-totems-${from}_a_${to}.csv`,
      ["Fecha", "Tótem", "Desde", "Hacia", "Cliente", "Usuario", "Notas"],
      filtered.map((m) => [
        dayjs(m.created_at).format("YYYY-MM-DD HH:mm"),
        m.totem?.code ?? "",
        m.from_status ? getStatusLabel(m.from_status) : "",
        getStatusLabel(m.to_status),
        m.client?.name ?? "",
        performerName(m.performed_by_profile),
        m.notes ?? "",
      ])
    );
  };

  return (
    <div className="space-y-4">
      {/* Filter bar */}
      <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end">
        <div className="flex flex-col gap-1">
          <label className="text-xs text-muted-foreground">Desde</label>
          <Input
            type="date"
            value={from}
            max={to}
            onChange={(e) => setFrom(e.target.value)}
            className="h-9 w-full sm:w-40"
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs text-muted-foreground">Hasta</label>
          <Input
            type="date"
            value={to}
            min={from}
            onChange={(e) => setTo(e.target.value)}
            className="h-9 w-full sm:w-40"
          />
        </div>
        <div className="flex flex-1 flex-col gap-1 sm:min-w-[220px]">
          <label className="text-xs text-muted-foreground">Buscar</label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Tótem, cliente, factura o nota..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-9 pl-9"
            />
          </div>
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs text-muted-foreground">Estado destino</label>
          <Select
            value={statusFilter}
            onValueChange={(v) => setStatusFilter(v as TotemStatus | "all")}
          >
            <SelectTrigger className="h-9 w-full sm:w-48">
              <SelectValue placeholder="Todos" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              {TOTEM_STATUSES.map((s) => (
                <SelectItem key={s} value={s}>
                  {getStatusLabel(s)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button
          variant="outline"
          className="h-9"
          onClick={handleExport}
          disabled={filtered.length === 0}
        >
          <Download className="mr-2 h-4 w-4" />
          Descargar CSV
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-4">
              <TableSkeleton rows={8} columns={6} />
            </div>
          ) : filtered.length === 0 ? (
            <div className="p-6">
              <EmptyState
                icon={History}
                title="No hay movimientos en este rango"
                description="Ajusta el rango de fechas o los filtros para ver el historial de movimientos de los tótems."
              />
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="whitespace-nowrap">Fecha</TableHead>
                      <TableHead>Tótem</TableHead>
                      <TableHead>Movimiento</TableHead>
                      <TableHead>Cliente</TableHead>
                      <TableHead>Usuario</TableHead>
                      <TableHead>Notas</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {pageItems.map((m) => (
                      <TableRow
                        key={m.id}
                        className="cursor-pointer hover:bg-muted/30"
                        onClick={() => navigate(`/totems/${m.totem_id}`)}
                      >
                        <TableCell className="whitespace-nowrap text-sm text-muted-foreground">
                          {dayjs(m.created_at).format("DD MMM YYYY, HH:mm")}
                        </TableCell>
                        <TableCell className="font-medium">
                          {m.totem?.code ?? "—"}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1.5">
                            {m.from_status && (
                              <>
                                <TotemStatusBadge
                                  status={m.from_status}
                                  size="sm"
                                />
                                <ArrowRight className="h-3.5 w-3.5 text-muted-foreground" />
                              </>
                            )}
                            <TotemStatusBadge status={m.to_status} size="sm" />
                          </div>
                        </TableCell>
                        <TableCell>{m.client?.name ?? "—"}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {performerName(m.performed_by_profile)}
                        </TableCell>
                        <TableCell className="max-w-[280px] text-sm text-muted-foreground">
                          {m.notes ?? "—"}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              {pageCount > 1 && (
                <div className="border-t p-4">
                  <TablePagination
                    page={page}
                    pageCount={pageCount}
                    totalItems={total}
                    pageSize={PAGE_SIZE}
                    onPageChange={setPage}
                    itemLabel="movimientos"
                  />
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
